import { ShieldCheckIcon, Typography } from "@codat/orchard-ui";
import { IntegrationBranding, useIntegrationQuery } from "api/company";
import { ApiError, ApiLink } from "api/shared";
import { AxiosError } from "axios";
import { useConfig } from "contexts/ConfigContext";
import { useCreateConnectionClickHandler } from "hooks/useCreateConnectionClickHandler";
import { useGATrackingEvent } from "hooks/useGATracking";
import useQuery from "hooks/useQuery";
import { useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { formatUrl, getPageConfiguration, usePageViewedEvent } from "utils";

import ErrorFallback from "components/ErrorFallback";
import ExitModal from "components/ExitModal";
import LinkPageRoot from "components/LinkPageRoot";
import SecondaryCta from "components/SecondaryCta";
import {
  WithSourceTypeEvent,
  WithSourceTypeInnerProps,
} from "components/WithSourceTypeEventContext";
import { CompanySourceTypeParams, ConsentParams, routes } from "config/urls";

import styles from "./Consent.module.css";

export const TermsAndConditionsLink = ({
  link,
}: {
  link?: ApiLink["link"];
}) => {
  if (link?.href?.trim()) {
    return (
      <SecondaryCta
        className={styles.link}
        data-testid="consents-terms-and-conditions"
        label={link.text ?? link.href}
        href={link.href}
        analyticsName="Terms and conditions"
        externalLink
      />
    );
  }

  return null;
};

type CompanyConsentProps = RouteComponentProps<ConsentParams> &
  WithSourceTypeInnerProps & {
    integration: IntegrationBranding | undefined;
    loading: boolean;
    error: AxiosError<ApiError> | undefined;
  };

const CompanyConsentContent: React.FC<CompanyConsentProps> = ({
  match,
  sourceTypes,
  currentSourceType,
  integration,
  loading,
  error,
}) => {
  const history = useHistory();

  const [isExitModal, setExitModal] = useState(false);

  const { state } = useConfig();

  const { logGAEvent } = useGATrackingEvent();

  const sourceType = integration?.sourceType;
  const platformName = integration?.name;
  const buttonImage = integration?.button;

  const [isLoading, handleNextButtonClick] = useCreateConnectionClickHandler({
    companyId: match.params.companyId,
    platformKey: match.params.platformKey,
    sourceType,
  });

  const queryString = useQuery();

  usePageViewedEvent({
    ready: !!integration?.sourceType && !loading,
    clientConfig: state.data,
    sourceTypes,
  });

  if (error) {
    return <ErrorFallback error={error} />;
  }

  const title =
    sourceType === "Banking"
      ? "Connect your bank account"
      : `Connect to ${platformName}`;

  const consentBody =
    sourceType === "Banking"
      ? "You will be asked to authorise access to the following data:"
      : `You will be asked to connect your ${platformName} account and authorize ${state.data?.clientName} access to the following data:`;

  const consentsBodyConfirmation = (() => {
    const consentsBody = state.data.pageConfiguration.dataAccess.consentsBody;
    if (!consentsBody) {
      return "";
    }
    switch (sourceType) {
      case "Accounting":
        return consentsBody.accounting;
      case "Banking":
        return consentsBody.banking;
      case "Commerce":
        return consentsBody.commerce;
      default:
        return "";
    }
  })();

  const consents: string[] =
    getPageConfiguration(state.data, "dataAccess", sourceType) ??
    new Array<string>();

  const handleBackButtonClick = sourceType
    ? () => {
        history.push(
          formatUrl<CompanySourceTypeParams>(
            routes.company.link,
            {
              companyId: match.params.companyId,
              sourceType: sourceType?.toLowerCase(),
            },
            queryString
          )
        );
      }
    : undefined;

  const handleExitButtonClick = state.data.hasCustomRedirect
    ? () => {
        logGAEvent("Link Flow", "ExitIcon", match.params.companyId);
        setExitModal(true);
      }
    : undefined;
  return (
    <>
      <LinkPageRoot
        title={loading ? "Loading..." : title}
        showLinkedAccounts
        onBackClick={handleBackButtonClick}
        onExitClick={handleExitButtonClick}
        primaryCtaProps={{
          onClick: handleNextButtonClick,
          isLoading,
          image: buttonImage,
          analyticsName: "Next",
        }}
        isComplete={!loading}
        currentSourceType={currentSourceType}
      >
        <div className={styles.wrapper}>
          <Typography
            data-testid="consent-body"
            variant="p"
            as="p"
            className={styles.body}
          >
            {consentBody}
          </Typography>

          {sourceType && (
            <div
              className={styles.consentContainer}
              data-testid="consents-container"
            >
              {Object.values(consents).map(
                (c, i) =>
                  c && (
                    <div key={i} className={styles.consentRow}>
                      <ShieldCheckIcon className={styles.consentIcon} />
                      <Typography
                        variant="p"
                        as="p"
                        className={styles.consentText}
                      >
                        {c}
                      </Typography>
                    </div>
                  )
              )}
            </div>
          )}
          {consentsBodyConfirmation && (
            <Typography
              data-testid="consent-permission-notice"
              variant="p"
              as="p"
              className={styles.privacy}
            >
              {consentsBodyConfirmation}
            </Typography>
          )}
          <TermsAndConditionsLink
            link={state.data?.pageConfiguration?.dataAccess?.termsAndConditions}
          />
        </div>
      </LinkPageRoot>
      {state.data.hasCustomRedirect && (
        <ExitModal
          isOpen={isExitModal}
          onClose={() => setExitModal(false)}
          companyId={match.params.companyId}
          sourceType={sourceType}
        ></ExitModal>
      )}
    </>
  );
};

export const CompanyConsent: React.FC<RouteComponentProps<ConsentParams>> = (
  props
) => {
  const [{ data: integration, loading, error }] = useIntegrationQuery({
    companyId: props.match.params.companyId,
    platformKey: props.match.params.platformKey,
  });

  return (
    <WithSourceTypeEvent
      sourceType={integration?.sourceType}
      eventName={() => `Link ${integration?.sourceType} consent page`}
    >
      {(sourceTypeProps) => (
        <CompanyConsentContent
          {...sourceTypeProps}
          {...props}
          integration={integration}
          loading={loading}
          error={error ?? undefined}
        />
      )}
    </WithSourceTypeEvent>
  );
};
